import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import matchupStyles from "../styles/matchups.css?url";
import { DocumentContent } from "../components/document-content";
import { LiveMatchupPreview } from "../components/matchups/live-preview";
import { useShell } from "../lib/document-route";
import { creatorPageHead, shellFrom } from "../lib/head";
import { loadMatchupPreview } from "../server/matchup-preview";

/** The LIVE route renders only its selected public guide and remains editable as a document. */
export const Route = createFileRoute("/live")({
  validateSearch: (search: Record<string, unknown>) => ({
    example: search.example === "1" ? "1" : undefined,
  }),
  loader: async () => {
    const data = await loadMatchupPreview();
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head = creatorPageHead(
      shell,
      loaderData.kind === "page"
        ? {
            pathname: "/live",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          }
        : {
            pathname: "/live",
            title: `Live Matchup | ${shell.creator.name}`,
            description: "Preview a free matchup game plan.",
          },
    );
    return {
      ...head,
      links: [
        ...(head.links || []),
        { rel: "stylesheet", href: matchupStyles, precedence: "matchups" },
      ],
    };
  },
  component: LivePage,
});
function LivePage() {
  const initial = Route.useLoaderData();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/live", "page");
  const { example } = Route.useSearch();
  const content = data.matchupPreview ? (
    <LiveMatchupPreview view={data.matchupPreview} expanded={example === "1"} />
  ) : null;
  return data.kind === "page" ? (
    <DocumentContent data={data} shell={shell} builtIn={content} />
  ) : (
    content
  );
}
